/**
 * Locale-aware tooltip formatting for BYOD charts.
 *
 * The design system's default chart tooltip prints the raw point value
 * (`{point.y}`) with no locale formatting. Every other analytics-genesis
 * chart overrides this with a `pointFormatter` that runs the value through
 * `numberToIntlString`, so grouping/decimal separators follow the viewer's
 * locale (see candidate-roi/roi-column-chart's `tooltipPointFormatter` /
 * `tooltipPointRateFormatter`). This applies the same treatment to BYOD's
 * UniversalChart.
 *
 * A single formatter covers both the plain and rate (`%`) cases because a
 * BYOD chart can mix units across series (e.g. a combo with count bars and a
 * rate line). Percent series are tagged in chart-data with
 * `tooltip.valueSuffix = '%'`; Highcharts surfaces that on the bound point as
 * `series.tooltipOptions.valueSuffix`, which we append here.
 */
import {numberToIntlString} from 'src/utils/number';

import css from './byod-chart.css';

// Minimal shape of the Highcharts point that `pointFormatter` is bound to.
// `tooltipOptions` is the merge of chart- and series-level tooltip options,
// so a percent series' `valueSuffix` is readable from it.
type ByodTooltipPoint = {
  y?: number | null;
  color?: string;
  series: {
    name: string;
    tooltipOptions?: {valueSuffix?: string};
  };
};

export function byodTooltipPointFormatter(): string {
  // @ts-expect-error - TS2683 - 'this' implicitly has type 'any' because it does not have a type annotation.
  const point: ByodTooltipPoint = this;
  const formattedValue = numberToIntlString(point.y);
  const suffix = point.series.tooltipOptions?.valueSuffix ?? '';
  return `
      <span class="${css.tooltipPoint}">
          <span style="background-color:${point.color};" class="${css.indicatorCircle}"></span>
          <span class="${css.tooltipContent}"> ${point.series.name}: ${formattedValue}${suffix}</span>
      </span>`;
}
